import React, { Suspense, useRef, useMemo, useEffect, useCallback, useState } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { Environment } from '@react-three/drei'
import * as THREE from 'three'
import { EffectComposer, Bloom } from '@react-three/postprocessing'
import { KernelSize } from 'postprocessing'
import { TitanCore } from './TitanCore'
import { SovereignPortal } from './SovereignPortal'
import { Tatiana } from './Tatiana'
import { Portal } from './Portal'
import AudioListener3D from './AudioListener3D'
import { useGenesisState } from '../hooks/useGenesisState'
import { useCameraControls } from '../hooks/useCameraControls'
import { usePortalCollision } from '../hooks/usePortalCollision'
import { usePointerLock } from '../hooks/usePointerLock'
import { TIME_OF_DAY_LIGHTING, ENVIRONMENT_COLORS, TimeOfDay, EnvironmentType } from '../types/world'

// Volumetric fog component with dynamic color
const VolumetricFog: React.FC<{ timeOfDay?: TimeOfDay; environment?: EnvironmentType }> = ({ 
  timeOfDay = 'night', 
  environment = 'void' 
}) => {
  const { scene } = useThree()
  const lightingConfig = TIME_OF_DAY_LIGHTING[timeOfDay]
  
  useEffect(() => {
    scene.fog = new THREE.FogExp2(lightingConfig.fogColor, lightingConfig.fogDensity)
    scene.background = new THREE.Color(lightingConfig.backgroundColor)
    
    return () => {
      scene.fog = null
    }
  }, [scene, lightingConfig])
  
  return null
}

// Ambient particle system for the void with environment colors
const AmbientParticles: React.FC<{ environment?: EnvironmentType }> = ({ environment = 'void' }) => {
  const ref = useRef<THREE.Points>(null)
  const envColor = ENVIRONMENT_COLORS[environment]
  
  const [geometry, material] = useMemo(() => {
    const count = 2000
    const positions = new Float32Array(count * 3)
    const colors = new Float32Array(count * 3)
    const sizes = new Float32Array(count)
    
    const colorObj = new THREE.Color(envColor)
    
    for (let i = 0; i < count; i++) {
      const theta = Math.random() * Math.PI * 2
      const phi = Math.acos(2 * Math.random() - 1)
      const radius = 10 + Math.random() * 40
      
      positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta)
      positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta)
      positions[i * 3 + 2] = radius * Math.cos(phi) - 15
      
      // Mix between cyan/purple and environment color
      const t = Math.random()
      colors[i * 3] = t * colorObj.r + (1 - t) * 0.2
      colors[i * 3 + 1] = t * colorObj.g + (1 - t) * 0.4
      colors[i * 3 + 2] = t * colorObj.b + (1 - t) * 0.8
      
      sizes[i] = 0.02 + Math.random() * 0.08
    }
    
    const geo = new THREE.BufferGeometry()
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3))
    geo.setAttribute('size', new THREE.BufferAttribute(sizes, 1))
    
    const mat = new THREE.PointsMaterial({
      size: 0.05,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true
    })
    
    return [geo, mat]
  }, [envColor])
  
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.getElapsedTime() * 0.005
      ref.current.rotation.x = Math.sin(state.clock.getElapsedTime() * 0.01) * 0.05
    }
  })
  
  return <points ref={ref} geometry={geometry} material={material} />
}

// Dynamic scene lighting based on time_of_day
// For Aether Void: minimal lighting, scene lit primarily by TitanCore/Ophanim
const SceneLighting: React.FC<{ timeOfDay?: TimeOfDay; environment?: EnvironmentType }> = ({
  timeOfDay = 'night',
  environment = 'void'
}) => {
  const lightRef = useRef<THREE.DirectionalLight>(null)
  const lightingConfig = TIME_OF_DAY_LIGHTING[timeOfDay]
  
  // For void environment: minimal ambient, let TitanCore/Ophanim be the light source
  const isVoid = environment === 'void'
  
  useFrame((state) => {
    const time = state.clock.getElapsedTime()
    if (lightRef.current && !isVoid) {
      lightRef.current.position.x = Math.sin(time * 0.1) * 10
      lightRef.current.position.z = Math.cos(time * 0.1) * 10
    }
  })
  
  if (isVoid) {
    // Aether Void: PBR lighting for metallic Ophanim
    return (
      <>
        {/* Ambient light for base illumination */}
        <ambientLight intensity={0.2} color="#ffffff" />
        
        {/* Directional light for gold metallic reflections */}
        <directionalLight position={[10, 10, 10]} intensity={2} color="#D4AF37" />
        
        {/* Environment for PBR reflections */}
        <Environment preset="night" environmentIntensity={0.1} />
      </>
    )
  }
  
  return (
    <>
      {/* Ambient - dynamic intensity based on time */}
      <ambientLight intensity={lightingConfig.ambientIntensity} />
      
      {/* Main dramatic light */}
      <directionalLight
        ref={lightRef}
        position={[5, 8, 5]}
        intensity={lightingConfig.directionalIntensity}
        color={lightingConfig.directionalColor}
        castShadow
      />
      
      {/* Purple rim light from below - less intense during day */}
      <spotLight
        position={[-5, -5, -5]}
        intensity={lightingConfig.directionalIntensity * 1.2}
        color={timeOfDay === 'eclipse' ? '#8B0000' : '#8A2BE2'}
        angle={Math.PI / 4}
        penumbra={0.5}
        distance={50}
      />
      
      {/* Environment-colored accent light */}
      <pointLight
        position={[0, 0, 2]}
        intensity={lightingConfig.directionalIntensity * 0.5}
        color={ENVIRONMENT_COLORS[environment]}
        distance={20}
      />
    </>
  )
}

// Camera controller with WASD + mouse look
const CameraController: React.FC<{
  onPositionUpdate: (pos: THREE.Vector3) => void
}> = ({ onPositionUpdate }) => {
  const { camera } = useThree()
  const { position, rotation, update } = useCameraControls(
    new THREE.Vector3(0, 0, 5),
    { moveSpeed: 3.0, lookSpeed: 0.002 }
  )
  
  const movementRef = useRef({ x: 0, y: 0 })
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (document.pointerLockElement) {
        movementRef.current.x += e.movementX
        movementRef.current.y += e.movementY
      }
    }
    
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])
  
  useFrame((state, delta) => {
    update(delta, movementRef.current.x, movementRef.current.y)
    movementRef.current.x = 0
    movementRef.current.y = 0
    camera.position.copy(position)
    camera.rotation.copy(rotation)
    onPositionUpdate(position)
  })
  
  return null
}

// Main scene component
const Scene: React.FC<{
  onPlayerPositionUpdate: (pos: THREE.Vector3) => void
}> = ({ onPlayerPositionUpdate }) => {
  const { 
    activePortal, 
    titanBeamActive, 
    isTatianaTyping, 
    isAiProcessing,
    worldSeed,
    portalState,
    generationProgress,
    updatePortalState
  } = useGenesisState()
  
  const timeOfDay = worldSeed?.time_of_day || 'night'
  const environment = worldSeed?.environment || 'void'
  
  return (
    <>
      <CameraController onPositionUpdate={onPlayerPositionUpdate} />
      
      {/* Dynamic volumetric fog */}
      <VolumetricFog timeOfDay={timeOfDay} environment={environment} />
      
      {/* Dynamic lighting */}
      <SceneLighting timeOfDay={timeOfDay} environment={environment} />
      
      
      {/* Titan Core */}
      <TitanCore
        position={[0, 4, -18]}
        scale={8}
        beamActive={titanBeamActive}
      />
      <SovereignPortal />
      
      {/* Tatiana - frontend guide near user, offset left above terminal */}
      <Tatiana
        position={[-3, 0, -5]}
        scale={1}
        isTyping={isTatianaTyping}
        isProcessing={isAiProcessing || portalState === 'generating'}
      />
      
      {/* Spawnable Portal with WorldSeed */}
      {activePortal && (
        <Portal 
          game={activePortal.game}
          color={activePortal.color}
          position={activePortal.position}
          active={activePortal.active}
          state={portalState}
          worldSeed={worldSeed || undefined}
          generationProgress={generationProgress}
          onStateChange={updatePortalState}
        />
      )}
      
      {/* Ambient Particles */}
      <AmbientParticles environment={environment} />
    </>
  )
}

// Loading fallback
const Loader: React.FC = () => {
  const ref = useRef<THREE.Mesh>(null)
  
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.getElapsedTime() * 0.5
      ref.current.rotation.x = state.clock.getElapsedTime() * 0.3
    }
  })
  
  return (
    <mesh ref={ref}>
      <octahedronGeometry args={[0.5, 0]} />
      <meshBasicMaterial color={0x00FFFF} wireframe />
    </mesh>
  )
}

// Main GenesisNode component
interface GenesisNodeProps {
  onInteractionStart?: () => void
  onInteractionEnd?: () => void
  onPortalEnter?: (game: string) => void
}

export const GenesisNode: React.FC<GenesisNodeProps> = ({
  onInteractionStart,
  onInteractionEnd,
  onPortalEnter
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { 
    activePortal, 
    setCameraPosition, 
    setInPortal, 
    loadGame, 
    hasInitialized,
    setHasInitialized,
    setPointerLocked,
    addSystemMessage,
    worldSeed
  } = useGenesisState()
  
  const [showInitialHint, setShowInitialHint] = useState(true)
  
  // Get background color based on world seed
  const backgroundColor = useMemo(() => {
    if (worldSeed?.time_of_day) {
      return TIME_OF_DAY_LIGHTING[worldSeed.time_of_day].backgroundColor
    }
    return '#050505'
  }, [worldSeed?.time_of_day])
  
  // Portal collision detection
  const { checkCollision } = usePortalCollision(onPortalEnter)
  
  // Handle pointer lock changes
  const handleLockChange = useCallback((isLocked: boolean) => {
    setPointerLocked(isLocked)
    
    if (!isLocked && hasInitialized) {
      addSystemMessage('[ SYSTEM ] : Connection Suspended. Re-initialize to resume navigation.')
    } else if (isLocked && !hasInitialized) {
      setHasInitialized(true)
      setShowInitialHint(false)
    }
  }, [hasInitialized, setPointerLocked, addSystemMessage, setHasInitialized])
  
  const { isLocked, requestLock } = usePointerLock({ onLockChange: handleLockChange })
  
  // Handle player position updates
  const handlePlayerPositionUpdate = useCallback((position: THREE.Vector3) => {
    setCameraPosition(position)
    
    if (activePortal) {
      const isColliding = checkCollision(position, activePortal)
      
      if (isColliding) {
        setInPortal(true, activePortal.game)
      } else {
        setInPortal(false)
      }
    }
  }, [activePortal, checkCollision, setCameraPosition, setInPortal])
  
  useEffect(() => {
    const handleMouseDown = () => onInteractionStart?.()
    const handleMouseUp = () => onInteractionEnd?.()
    const handleTouchStart = () => onInteractionStart?.()
    const handleTouchEnd = () => onInteractionEnd?.()
    
    const container = containerRef.current
    if (container) {
      container.addEventListener('mousedown', handleMouseDown)
      container.addEventListener('mouseup', handleMouseUp)
      container.addEventListener('touchstart', handleTouchStart)
      container.addEventListener('touchend', handleTouchEnd)
    }
    
    return () => {
      if (container) {
        container.removeEventListener('mousedown', handleMouseDown)
        container.removeEventListener('mouseup', handleMouseUp)
        container.removeEventListener('touchstart', handleTouchStart)
        container.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [onInteractionStart, onInteractionEnd])
  
  const handleCanvasClick = useCallback(() => {
    if (!isLocked) {
      requestLock().catch(err => {
        console.warn('[GenesisNode] Pointer lock request failed:', err)
      })
    }
  }, [isLocked, requestLock])
  
  return (
    <div 
      ref={containerRef}
      className="genesis-canvas-container"
      style={{ cursor: isLocked ? 'none' : 'crosshair' }}
      onClick={handleCanvasClick}
    >
      <Canvas
        tabIndex={0}
        style={{ outline: 'none' }}
        camera={{ position: [0, 0, 5], fov: 60 }}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: 'high-performance',
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.2
        }}
        dpr={[1, 2]}
        onClick={() => {
          const canvas = document.querySelector('canvas')
          canvas?.focus()
        }}
      >
        <color attach="background" args={[backgroundColor]} />
        <AudioListener3D />
        <Suspense fallback={<Loader />}>
          <Scene onPlayerPositionUpdate={handlePlayerPositionUpdate} />
        </Suspense>
        
        {/* Post-processing effects */}
        <EffectComposer>
          <Bloom
            intensity={1.5}
            kernelSize={KernelSize.LARGE}
            luminanceThreshold={0.1}
            luminanceSmoothing={0.4}
            mipmapBlur={true}
          />
        </EffectComposer>
      </Canvas>
      
      {showInitialHint && !isLocked && !hasInitialized && (
        <div 
          className="pointer-lock-hint"
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            color: 'rgba(0, 255, 255, 0.5)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '10px',
            letterSpacing: '0.1em',
            pointerEvents: 'none'
          }}
        >
          [ CLICK TO CAPTURE MOUSE ]
        </div>
      )}
      
      {!isLocked && hasInitialized && (
        <div 
          className="pointer-lock-resume-hint"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'rgba(0, 255, 255, 0.3)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '14px',
            letterSpacing: '0.2em',
            pointerEvents: 'none',
            textAlign: 'center',
            animation: 'pulse 2s ease-in-out infinite'
          }}
        >
          [ CLICK TO RESUME ]
        </div>
      )}
    </div>
  )
}

export default GenesisNode
